/**
 * Department access-control rules for the NECS Admin dashboard.
 *
 * Source of truth for which department pages each admin can open.
 * The menu is rendered identically for everyone, but locked items
 * display a lock icon and emit a toast on click.
 *
 *  - Executive Office (EXC)   → full access (every department)
 *  - Human Resources (HRX)    → all departments EXCEPT Executive Office
 *  - Information Technology (TEC) → own dept by default;
 *      additionally Executive + HR ONLY when `user.it_authorized` is true
 *      (must be granted by HR and Legal & Compliance offline)
 *  - Legal & Compliance (LEG) → own + ability to audit IT authorizations
 *  - All other departments    → own department only
 *  - Home                     → always available to every signed-in admin
 */
import { parseBadge } from './badgeParser';

// Department keys used by the sidebar (lowercase, kebab) → 3-letter badge code
export const DEPT_KEY_TO_CODE = {
  executive:         'EXC',
  operations:        'OPS',
  finance:           'FIN',
  hr:                'HRX',
  it:                'TEC',
  legal:             'LEG',
  sales:             'SLS',
  marketing:         'MKT',
  'customer-service':'CSV',
  procurement:       'PRC',
  safety:            'SAF',
  training:          'TRN',
};

export function getUserDeptCode(user) {
  if (!user) return null;
  if (user.admin_department) return DEPT_KEY_TO_CODE[user.admin_department] || null;
  // 🔑 Role-first: `admin` and `company_manager` are always CEO/Executive-level,
  // no matter what their `badge_number` looks like. This keeps access working
  // for legacy badges (e.g. "CEO-001", "NE-001") that predate the modern
  // `NECS-A-EXC-CEO-TX-0001` schema — the source of truth for authorization
  // is the role field, not the printed badge on someone's shirt.
  if (user.role === 'admin' || user.role === 'company_manager') return 'EXC';

  const parsed = parseBadge(user.badge_number);
  if (parsed.ok && parsed.kind === 'admin' && parsed.dept) {
    return parsed.dept;
  }
  return null;
}

export function canAccessDepartment(user, deptKey) {
  if (deptKey === 'home') return true;
  if (user?.admin_department) return user.admin_department === 'executive' || user.admin_department === deptKey;
  const userDept = getUserDeptCode(user);
  if (!userDept) return false;
  const targetCode = DEPT_KEY_TO_CODE[deptKey];
  if (!targetCode) return false;

  // Own department: always
  if (userDept === targetCode) return true;

  // Executive Office: full access
  if (userDept === 'EXC') return true;

  // Human Resources: everything except Executive Office
  if (userDept === 'HRX') {
    return targetCode !== 'EXC';
  }

  // Information Technology: requires explicit authorization granted by
  // BOTH Human Resources AND Legal & Compliance to view Exec / HR.
  if (userDept === 'TEC' && user?.it_authorized) {
    return targetCode === 'EXC' || targetCode === 'HRX';
  }

  // Legal & Compliance: can audit IT authorizations (sees IT)
  if (userDept === 'LEG' && targetCode === 'TEC') return true;

  return false;
}

export function lockReasonFor(user, deptKey) {
  const userDept = getUserDeptCode(user);
  if (!userDept) return 'Sign in with an admin badge to unlock departments.';
  if (deptKey === 'executive') {
    return 'Executive Office is restricted to C-Suite officers.';
  }
  if (userDept === 'TEC') {
    return 'IT access to this department requires written authorization from Human Resources and Legal & Compliance.';
  }
  return 'You do not have clearance for this department. Contact your administrator.';
}
